/**
 * Thunks for the /admin/app-config definition sync.
 *
 * Five constraints shape everything here.
 *
 * 1. `callGraphQLApi` has TWO failure exits, and neither can be inferred from
 *    the other. It throws on most GraphQL errors even with `throwOnError:
 *    false` (its own header says so, and there is a standing TODO on the
 *    throw) — but when EVERY error is named `not_found` it dispatches failure,
 *    logs, and returns `undefined` without throwing. `errors.NotFound` in
 *    aiWARE serialises to exactly that name, and the definition DAL raises it
 *    on ordinary paths: a non-superadmin update whose configKey matches no row
 *    in their own org, and any create/update/delete whose org guid cannot be
 *    resolved. So a try/catch alone reports a refused mutation as a success.
 *    Every call here therefore catches the throw AND asserts on the response;
 *    success is never inferred from the absence of an exception.
 * 2. The read query's server-side `limit` defaults to 30 and the manifest
 *    alone expects 26 definitions, so the scan pages — and a scan that cannot
 *    be completed fails rather than returning what it has. A truncated scan
 *    reports registered keys as missing and offers to re-register them, which
 *    is worse than no answer. That applies to a failed page AND to exhausting
 *    the page budget.
 * 3. `applicationConfigDefinitionDelete` answers with an OperationResult, so a
 *    refusal arrives as `success: false` on a perfectly successful response,
 *    not as a throw. It is read explicitly; a try/catch alone would report a
 *    refused delete as having worked.
 * 4. No `orgId` is sent on any query or mutation — an acceptance criterion,
 *    pinned by a unit test. `appId` comes from runtime config, never from the
 *    manifest. READ THAT PRECISELY: it is a statement about what we SEND, and
 *    it stopped implying what it used to imply. `applicationConfigDefinition`
 *    ignores the caller's org entirely (its SQL has no `organization_guid`
 *    predicate), but `applicationConfig` resolves an absent `orgId` to the
 *    CALLER'S org. So omitting it means "the whole instance" on one query and
 *    "whichever org you are in" on the other, and the page's copy has to say
 *    so rather than claiming it reads no organization data.
 * 5. A scan is TWO reads, and only the first of them is allowed to fail it.
 *    `value`/`valueJSON` are not on `ApplicationConfigDefinition`, so telling
 *    an operator whether an org has overridden a default needs a second query
 *    against `ApplicationConfig`. The definitions are what the page is for, so
 *    a failed values read reports `valuesUnavailable` and the page says it
 *    cannot tell — rather than failing the working half, or returning an empty
 *    map, which would claim every org takes the default.
 */
import { sagaIntl } from '@i18n';
import callGraphQLApi, {
  GQLApiDispatch,
  GQLApiGetState,
} from '@helpers/callGraphQLApi';
import {
  ADD_DEFINITIONS_FAILURE,
  ADD_DEFINITIONS_SUCCESS,
  CHECK_ROOT_ORGANIZATION_RESULT,
  DELETE_DEFINITION_FAILURE,
  DELETE_DEFINITION_SUCCESS,
  SCAN_DEFINITIONS_FAILURE,
  SCAN_DEFINITIONS_SUCCESS,
  UPDATE_DEFINITION_FAILURE,
  UPDATE_DEFINITION_SUCCESS,
} from '../actions';
import { LiveDefinition, ManifestDefinition, RootOrgStatus } from '../models';
import { selectVeritoneAppId } from '../selectors';
import {
  CREATE_CONFIG_DEFINITIONS,
  CreateConfigDefinitionsResponse,
  DELETE_CONFIG_DEFINITION,
  DeleteConfigDefinitionResponse,
  RETRIEVE_CONFIG_DEFINITIONS,
  RETRIEVE_CONFIG_VALUES,
  RETRIEVE_ROOT_ORGANIZATION,
  RetrieveConfigDefinitionsResponse,
  RetrieveConfigValuesResponse,
  RetrieveRootOrganizationResponse,
  UPDATE_CONFIG_DEFINITION,
  UpdateConfigDefinitionResponse,
} from './queries';

const NOOP = 'noop';

/** Asked-for page size. The server may return fewer; the loop copes. */
export const DEFINITION_PAGE_SIZE = 100;

/** Stops a server that ignores `offset` from looping forever. */
export const DEFINITION_MAX_PAGES = 20;

/** The API's own words where we have them, and never an empty string. */
export function messageOf(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'string' && error) {
    return error;
  }
  return fallback;
}

/**
 * The fallback messages this layer produces, resolved through formatjs.
 *
 * Functions rather than constants because the locale is read per call:
 * `sagaIntl()` builds an intl from the stored language, and a module-level
 * constant would freeze whichever locale was active at import time. These are
 * the ONLY strings this layer writes — everything else an operator reads on a
 * failed row is the API's own text, verbatim.
 */

/** The call came back, but the server did not report writing what we asked for. */
export const notWrittenMessage = (): string =>
  sagaIntl().formatMessage({
    id: 'adminErrorNotWritten',
    defaultMessage:
      'The API answered without confirming the change, so it did not take effect.',
  });

/**
 * What we tell the operator when `callGraphQLApi` swallowed the error.
 *
 * It returns `undefined` only on the `not_found` branch, having already
 * consumed the API's own text, so there is nothing verbatim left to show. This
 * says what is known — nothing was written — and points at the likeliest cause
 * without asserting it: definitions are owned by the organization that
 * registered them, and the scan returns every org's.
 */
/**
 * A create that came back with the key absent from `records`.
 *
 * Measured on stage us-gov-2: re-running create on a key that already exists
 * returns `records: []` with no error and writes nothing, so this is by far
 * the likeliest cause. Worded as the likeliest cause rather than the certain
 * one, because an empty `records` could mean something else.
 */
export const notRegisteredMessage = (): string =>
  sagaIntl().formatMessage({
    id: 'adminErrorNotRegistered',
    defaultMessage:
      'The API did not report registering this key. It is most often already registered — re-scan to see the current state.',
  });

export const noConfirmationMessage = (): string =>
  sagaIntl().formatMessage({
    id: 'adminErrorNoConfirmation',
    defaultMessage:
      'The API returned no confirmation, so nothing was changed. The definition may belong to another organization, or may not exist for yours.',
  });

/** Shapes a manifest definition into an ApplicationConfigDefinitionCreate input. */
export function toCreateInput(
  definition: ManifestDefinition,
  appId: string
): Record<string, unknown> {
  const input: Record<string, unknown> = {
    appId,
    configKey: definition.configKey,
    configType: definition.configType,
    configLevel: definition.configLevel,
    required: definition.required,
    secured: definition.secured,
    description: definition.description,
  };
  // Omitted rather than sent as null: the manifest distinguishes "declares no
  // default" from a value, and so should the definition we register.
  if (definition.defaultValue !== undefined) {
    input.defaultValue = definition.defaultValue;
  }
  return input;
}

/**
 * The mutable half of an ApplicationConfigDefinitionUpdateInput.
 *
 * `defaultValue` is ALWAYS sent, as `null` where the manifest declares none —
 * unlike `toCreateInput`, which omits it. The two mean opposite things on the
 * two mutations: omitting it from a create registers a definition with no
 * default, but omitting it from an update says "leave whatever is there". So
 * omitting it here made one drift unfixable — a manifest entry with no default
 * (`featureFlags`, `transcriptionEngineOptions`) against an environment that
 * has one would report drift, Update would send nothing for that field, and
 * the re-scan would report the same drift again. The button would look broken
 * because it would be.
 */
export function toUpdateFields(
  definition: ManifestDefinition
): Record<string, unknown> {
  return {
    configType: definition.configType,
    configLevel: definition.configLevel,
    required: definition.required,
    secured: definition.secured,
    description: definition.description,
    defaultValue: definition.defaultValue ?? null,
  };
}

/**
 * This organization's own config values, keyed by configKey.
 *
 * BEST EFFORT, unlike the definitions read. Every failure mode — a throw, a
 * swallowed `not_found`, a page budget exhausted — returns `undefined`, and the
 * caller reports the scan as succeeded with values unavailable. The definitions
 * are what the page is for; failing the whole scan because the second read did
 * not answer would take away the working half to protect the extra.
 *
 * The distinction that must survive is `undefined` (we do not know) versus an
 * empty map (we asked, nothing is overridden). Returning an empty map on
 * failure would silently claim every org takes the default.
 */
async function readOrgValues(
  appId: string,
  dispatch: GQLApiDispatch,
  getState: GQLApiGetState
): Promise<
  Map<string, { value?: string | null; valueJSON?: unknown }> | undefined
> {
  const byKey = new Map<
    string,
    { value?: string | null; valueJSON?: unknown }
  >();
  /**
   * Rows the server has handed back, which is NOT `byKey.size`.
   *
   * The two diverge because this query filters `organization_guid` but not
   * `user_id`, and we send no `userId` — so a `configLevel: 'User'` definition
   * comes back once per user who has set it. `folderViewUser` and
   * `folderViewTilesUser` are both User-level and both in the manifest, so an
   * org where a hundred people have toggled tile mode returns a hundred rows
   * for one configKey, all but the first dropped by the dedup below.
   *
   * Paging by the deduped size therefore under-advances the cursor and re-reads
   * rows already seen; with more than a page of them it advances by ZERO and
   * the loop burns its whole budget on one offset, ending in
   * `valuesUnavailable` for an org whose values read perfectly.
   */
  let consumed = 0;

  for (let page = 0; page < DEFINITION_MAX_PAGES; page += 1) {
    let response: RetrieveConfigValuesResponse | undefined;
    try {
      response = await callGraphQLApi<RetrieveConfigValuesResponse>({
        actionTypes: [NOOP, NOOP, NOOP],
        query: RETRIEVE_CONFIG_VALUES,
        variables: { appId, offset: consumed, limit: DEFINITION_PAGE_SIZE },
        dispatch,
        getState,
        throwOnError: false,
      });
    } catch {
      return undefined;
    }

    const pageRecords = response?.applicationConfig?.records;
    if (!pageRecords) {
      // `not_found` is swallowed into `undefined` by callGraphQLApi. For this
      // read that genuinely means "this org has overridden nothing", which is
      // an answer rather than a failure.
      return byKey;
    }

    consumed += pageRecords.length;
    for (const record of pageRecords) {
      // First row per key wins. Without a `config_level` to sort on — the
      // `includeDefaults: false` query does not join the definition table —
      // there is nothing better to prefer, and any row for this key is this
      // org's own, so "has a custom value" is right either way.
      if (record.configKey && !byKey.has(record.configKey)) {
        byKey.set(record.configKey, {
          value: record.value,
          valueJSON: record.valueJSON,
        });
      }
    }

    if (pageRecords.length < DEFINITION_PAGE_SIZE) {
      return byKey;
    }
  }

  // Out of pages rather than out of records: a partial answer here would show
  // overridden keys as taking the default, so say we do not know.
  return undefined;
}

/**
 * Reads every definition registered for the Redact app in this environment,
 * then this organization's own values for them.
 *
 * A failed definitions page fails the whole scan rather than returning what it
 * has: a partial scan is indistinguishable from an environment genuinely
 * missing those keys, and the page would then offer to "fix" definitions that
 * exist. The values read is best effort — see `readOrgValues`.
 */
export const scanDefinitions =
  () => async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const appId = selectVeritoneAppId(getState());
    const records: LiveDefinition[] = [];
    let complete = false;

    // Rows the SERVER has handed back, which is what its offset counts. Not
    // `records.length` — this loop drops records with no `configKey` (the field
    // is nullable, which is why the filter exists), so the two diverge the
    // moment one appears, and every later page re-reads rows already seen.
    // They surface as `duplicateKeys`, i.e. the page inventing a finding on an
    // environment that has none. `readOrgValues` below keeps the same counter
    // for the same reason.
    let consumed = 0;
    for (let page = 0; page < DEFINITION_MAX_PAGES; page += 1) {
      let response: RetrieveConfigDefinitionsResponse | undefined;
      try {
        response = await callGraphQLApi<RetrieveConfigDefinitionsResponse>({
          actionTypes: [NOOP, NOOP, NOOP],
          query: RETRIEVE_CONFIG_DEFINITIONS,
          variables: {
            appId,
            offset: consumed,
            limit: DEFINITION_PAGE_SIZE,
          },
          dispatch,
          getState,
          throwOnError: false,
        });
      } catch (error) {
        dispatch(
          SCAN_DEFINITIONS_FAILURE({
            // Deliberately NOT the card's own "Could not read the definitions
            // for this environment" heading: that is already on screen, and a
            // non-Error throw printed it twice, once translated and once not.
            message: messageOf(
              error,
              sagaIntl().formatMessage({
                id: 'adminErrorScanUnknown',
                defaultMessage: 'The request failed without an error message.',
              })
            ),
          })
        );
        return;
      }

      const pageRecords = response?.applicationConfigDefinition?.records;
      if (!pageRecords) {
        // callGraphQLApi returns undefined for `not_found`. Nothing registered
        // is a legitimate answer here, so stop rather than fail.
        complete = true;
        break;
      }

      consumed += pageRecords.length;

      // `configKey` is nullable in the schema. A record without one cannot be
      // bucketed, keyed or deleted, so it is dropped here rather than carried
      // as a null through every consumer — which is exactly why the cursor
      // above counts what the server returned rather than what survived.
      records.push(
        ...(pageRecords as LiveDefinition[]).filter(
          (record) => record.configKey
        )
      );

      if (pageRecords.length < DEFINITION_PAGE_SIZE) {
        complete = true;
        break;
      }
    }

    if (!complete) {
      // The loop ran out of pages, not out of records. Reporting what we have
      // would label a partial scan complete, and every definition past the cap
      // would show as Missing with an Add button beside it.
      dispatch(
        SCAN_DEFINITIONS_FAILURE({
          message: sagaIntl().formatMessage(
            {
              id: 'adminErrorScanTruncated',
              defaultMessage:
                'Stopped after {pages} pages with {count} definitions read, so this environment has more than the scan can show. Reporting a partial scan would mark registered keys as missing.',
            },
            { pages: DEFINITION_MAX_PAGES, count: records.length }
          ),
        })
      );
      return;
    }

    const values = await readOrgValues(appId, dispatch, getState);

    dispatch(
      SCAN_DEFINITIONS_SUCCESS({
        // Merged here rather than held as a second list, so every consumer —
        // diff, buckets, row — sees one record per key.
        records: values
          ? records.map((record) => {
              const override = values.get(record.configKey);
              return override ? { ...record, ...override } : record;
            })
          : records,
        valuesUnavailable: !values,
        scannedAt: new Date().toISOString(),
      })
    );
  };

/**
 * Answers whether this session is in the instance root organization.
 *
 * A failure — including a cluster that does not expose `isRootOrganization` —
 * is reported as `unknown`, never as `notRoot`. Guessing `notRoot` would
 * disable the page's controls on no evidence at all.
 */
export const checkRootOrganization =
  () => async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    let response: RetrieveRootOrganizationResponse | undefined;
    try {
      response = await callGraphQLApi<RetrieveRootOrganizationResponse>({
        actionTypes: [NOOP, NOOP, NOOP],
        query: RETRIEVE_ROOT_ORGANIZATION,
        variables: {},
        dispatch,
        getState,
        throwOnError: false,
      });
    } catch {
      dispatch(CHECK_ROOT_ORGANIZATION_RESULT({ status: 'unknown' }));
      return;
    }

    const organization = response?.me?.organization;
    const organizationName = organization?.name ?? undefined;
    const status: RootOrgStatus =
      typeof organization?.isRootOrganization === 'boolean'
        ? organization.isRootOrganization
          ? 'root'
          : 'notRoot'
        : 'unknown';

    dispatch(CHECK_ROOT_ORGANIZATION_RESULT({ status, organizationName }));
  };

/** Registers one or more definitions in a single call. */
export const createDefinitions =
  (definitions: readonly ManifestDefinition[]) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const appId = selectVeritoneAppId(getState());
    const configKeys = definitions.map((d) => d.configKey);

    let response: CreateConfigDefinitionsResponse | undefined;
    try {
      response = await callGraphQLApi<CreateConfigDefinitionsResponse>({
        actionTypes: [NOOP, NOOP, NOOP],
        query: CREATE_CONFIG_DEFINITIONS,
        variables: { input: definitions.map((d) => toCreateInput(d, appId)) },
        dispatch,
        getState,
        throwOnError: false,
      });
    } catch (error) {
      dispatch(
        ADD_DEFINITIONS_FAILURE({
          configKeys,
          message: messageOf(error, 'The definitions could not be registered.'),
        })
      );
      return;
    }

    // Assert, do not infer. `records` names what the server actually wrote; a
    // missing key means that definition did not land, whatever the transport
    // reported.
    const written = new Set(
      (response?.applicationConfigDefinitionCreate?.records ?? []).map(
        (record) => record.configKey
      )
    );
    const absent = configKeys.filter((key) => !written.has(key));
    if (absent.length > 0) {
      dispatch(
        ADD_DEFINITIONS_FAILURE({
          configKeys: absent,
          message: response ? notRegisteredMessage() : noConfirmationMessage(),
        })
      );
      if (absent.length === configKeys.length) {
        return;
      }
    }

    const succeeded = configKeys.filter((key) => written.has(key));
    if (succeeded.length > 0) {
      dispatch(ADD_DEFINITIONS_SUCCESS({ configKeys: succeeded }));
    }
  };

/** Amends a registered definition so it matches the manifest again. */
export const updateDefinition =
  (definition: ManifestDefinition) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const appId = selectVeritoneAppId(getState());

    let response: UpdateConfigDefinitionResponse | undefined;
    try {
      response = await callGraphQLApi<UpdateConfigDefinitionResponse>({
        actionTypes: [NOOP, NOOP, NOOP],
        query: UPDATE_CONFIG_DEFINITION,
        variables: {
          input: [
            {
              filter: { appId, configKey: definition.configKey },
              update: toUpdateFields(definition),
            },
          ],
        },
        dispatch,
        getState,
        throwOnError: false,
      });
    } catch (error) {
      dispatch(
        UPDATE_DEFINITION_FAILURE({
          configKeys: [definition.configKey],
          message: messageOf(
            error,
            sagaIntl().formatMessage({
              id: 'adminErrorUpdateFailed',
              defaultMessage: 'The definition could not be updated.',
            })
          ),
        })
      );
      return;
    }

    const written = (
      response?.applicationConfigDefinitionUpdate?.records ?? []
    ).some((record) => record.configKey === definition.configKey);
    if (!written) {
      dispatch(
        UPDATE_DEFINITION_FAILURE({
          configKeys: [definition.configKey],
          message: response ? notWrittenMessage() : noConfirmationMessage(),
        })
      );
      return;
    }

    dispatch(UPDATE_DEFINITION_SUCCESS({ configKey: definition.configKey }));
  };

/**
 * Writes ONE field: the definition's default, to a value of the operator's
 * choosing rather than the manifest's.
 *
 * Deliberately not `toUpdateFields`, which sends six. This mutation is a
 * partial update selected by configKey, so sending only `defaultValue` leaves
 * everything else exactly as the environment has it — an operator setting a
 * per-environment default is not asking for `configType` or `description` to be
 * reconciled at the same time.
 *
 * The platform's truthy-check SET clause still applies: an empty string is
 * dropped while the mutation reports success. The dialog refuses to submit one
 * rather than letting this layer report a write that did not happen.
 */
export const setDefinitionDefault =
  (payload: {
    configKey: string;
    defaultValue?: string;
    defaultValueJSON?: unknown;
  }) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const appId = selectVeritoneAppId(getState());
    const { configKey, defaultValue, defaultValueJSON } = payload;
    const update =
      defaultValueJSON === undefined ? { defaultValue } : { defaultValueJSON };

    let response: UpdateConfigDefinitionResponse | undefined;
    try {
      response = await callGraphQLApi<UpdateConfigDefinitionResponse>({
        actionTypes: [NOOP, NOOP, NOOP],
        query: UPDATE_CONFIG_DEFINITION,
        variables: { input: [{ filter: { appId, configKey }, update }] },
        dispatch,
        getState,
        throwOnError: false,
      });
    } catch (error) {
      dispatch(
        UPDATE_DEFINITION_FAILURE({
          configKeys: [configKey],
          message: messageOf(
            error,
            sagaIntl().formatMessage({
              id: 'adminErrorUpdateFailed',
              defaultMessage: 'The definition could not be updated.',
            })
          ),
        })
      );
      return;
    }

    // Assert, do not infer — same reason as every other write here.
    const written = (
      response?.applicationConfigDefinitionUpdate?.records ?? []
    ).some((record) => record.configKey === configKey);
    if (!written) {
      dispatch(
        UPDATE_DEFINITION_FAILURE({
          configKeys: [configKey],
          message: response ? notWrittenMessage() : noConfirmationMessage(),
        })
      );
      return;
    }

    dispatch(UPDATE_DEFINITION_SUCCESS({ configKey }));
  };

/** Unregisters a definition for the whole environment. */
export const deleteDefinition =
  (configKey: string) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const appId = selectVeritoneAppId(getState());
    let response: DeleteConfigDefinitionResponse | undefined;

    try {
      response = await callGraphQLApi<DeleteConfigDefinitionResponse>({
        actionTypes: [NOOP, NOOP, NOOP],
        query: DELETE_CONFIG_DEFINITION,
        variables: { input: { appId, configKey } },
        dispatch,
        getState,
        throwOnError: false,
      });
    } catch (error) {
      dispatch(
        DELETE_DEFINITION_FAILURE({
          configKeys: [configKey],
          message: messageOf(
            error,
            sagaIntl().formatMessage({
              id: 'adminErrorDeleteFailed',
              defaultMessage: 'The definition could not be deleted.',
            })
          ),
        })
      );
      return;
    }

    // Only an explicit `success: true` is a success. A refusal arrives as
    // `success: false` on an otherwise fine response; a swallowed `not_found`
    // arrives as no response at all; and the field is nullable, so
    // `{ applicationConfigDefinitionDelete: null }` is a third way to mean no.
    const result = response?.applicationConfigDefinitionDelete;
    if (result?.success !== true) {
      dispatch(
        DELETE_DEFINITION_FAILURE({
          configKeys: [configKey],
          message:
            result?.msg ||
            result?.code ||
            (response
              ? sagaIntl().formatMessage({
                  id: 'adminErrorDeleteRefused',
                  defaultMessage: 'The environment refused the delete.',
                })
              : noConfirmationMessage()),
        })
      );
      return;
    }

    dispatch(DELETE_DEFINITION_SUCCESS({ configKey }));
  };
